import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/* Poster-like snap: quick entrances, minimal easing */
export const FadeUp = ({ children, delay = 0, className, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.35, delay, ease: [0.2, 0.8, 0.2, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const CountUp = ({ to, suffix = "", prefix = "", duration = 1.4, className, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setVal(to);
      return;
    }
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, reduce, duration]);

  return (
    <span ref={ref} className={`tabular-nums ${className || ""}`} data-testid={testId}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
};

/* Editorial section heading: tiny kicker + huge Bebas title */
export const SectionHeading = ({ eyebrow, title, sub, light = false }) => (
  <FadeUp className="max-w-3xl">
    {eyebrow && (
      <p
        className={`text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-4 ${
          light ? "text-[hsl(var(--kerrys-sun))]" : "text-[hsl(var(--kerrys-sea))]"
        }`}
      >
        {eyebrow}
      </p>
    )}
    <h2
      className={`font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] uppercase ${
        light ? "text-background" : "text-foreground"
      }`}
    >
      {title}
    </h2>
    {sub && (
      <p className={`mt-5 text-base sm:text-lg leading-relaxed max-w-xl ${light ? "text-background/70" : "text-muted-foreground"}`}>
        {sub}
      </p>
    )}
  </FadeUp>
);
